import type { FC, PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HashLoader } from "react-spinners";
import { layoutPage } from "@design-system";
import { UserDropdown } from "@components";
import { useUserSession } from "../../context/UserContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useOnboardingStatus } from "../../hooks";
import primaryLogo from "../../assets/logo/logibud_primary_darkblue.svg";
import secondaryLogo from "../../assets/logo/logibud_secondary_white.svg";

/**
 * Full-screen shell for the onboarding wizard. Keeps the flow uninterrupted
 * (no sidebar / navigation) while still requiring an authenticated session and
 * an active workspace. A workspace that is already fully set up on arrival is
 * redirected to the dashboard so the wizard cannot be re-entered manually.
 */
export const OnboardingLayout: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation(["onboarding", "navigation"]);
  const { userSession, isAuthenticated, authReady } = useUserSession();
  const user = userSession?.user;
  const { activeWorkspace } = useWorkspace();
  const status = useOnboardingStatus();

  const isAuthed = Boolean(userSession && isAuthenticated());
  const workspaceId = activeWorkspace?.workspaceId ?? "";

  // Decide once, after the required data first resolves, whether the workspace
  // was already complete on arrival. This avoids bouncing a user who completes
  // the final step in-session (status flips to complete) off the success view.
  const [decided, setDecided] = useState(false);
  const [redirectHome, setRedirectHome] = useState(false);

  useEffect(() => {
    if (decided) return;
    if (!authReady || !isAuthed || !workspaceId) return;
    if (status.isResolving) return;
    setDecided(true);
    if (!status.requiresInitialSetup) setRedirectHome(true);
  }, [
    decided,
    authReady,
    isAuthed,
    workspaceId,
    status.isResolving,
    status.requiresInitialSetup,
  ]);

  const loadingScreen = (
    <div className={`min-h-screen ${layoutPage}`}>
      <div className="flex h-screen flex-col items-center justify-center">
        <HashLoader
          color="#7214FF"
          size={100}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </div>
    </div>
  );

  if (!authReady) return loadingScreen;
  if (!isAuthed) return <Navigate to="/login" />;
  if (!workspaceId) return <Navigate to="/load_workspace" />;
  if (redirectHome) return <Navigate to="/" />;
  if (!decided) return loadingScreen;

  return (
    <div className={`min-h-screen ${layoutPage}`}>
      <header className="flex items-center justify-between px-4 py-4 sm:px-8">
        <div>
          <img
            src={primaryLogo}
            alt={t("onboarding:brand")}
            className="h-8 w-auto dark:hidden"
          />
          <img
            src={secondaryLogo}
            alt={t("onboarding:brand")}
            className="hidden h-8 w-auto dark:block"
          />
        </div>
        {/* Keep workspace switching available so multi-workspace users are
            never trapped in onboarding for the wrong workspace. */}
        {user ? <UserDropdown {...user} variant="workspace" /> : null}
      </header>
      <div className="mx-auto flex max-w-2xl flex-col px-4 pb-16 pt-6 sm:px-8 sm:pt-10">
        {children}
      </div>
    </div>
  );
};

export default OnboardingLayout;
